import React, {useEffect, useMemo, useState} from "react";
import Navbar from "@/components/Navbar/Navbar";
import SideMenu from "@/components/MyAccount/SideMenu";
import ApiHttpClient from "@/HttpClient";
import {toast} from "@/components/ui/use-toast";
import {debounce} from "lodash";

type ProductPrice = {
    id: number;
    name: string;
    gama: string;
    price: number;
    vatPercent: number;
};

type ArticleSearchItem = {
    id: number;
    shortName?: string;
    longName?: string;
    displayName?: string;
    thresholdPrice1?: number;
    vatPercent?: number;
    hierarchyLocal?: {
        marketLabel?: string;
    };
};

type ArticleSearchResponse = {
    items: ArticleSearchItem[];
    totalPages: number;
};

const UM_WORKING_POINT_ID = "70002";
const UM_ASSORTMENT_CODE = "UM";

const SearchInput: React.FC<{
    setSearchArticle: (value: string) => void;
    setIsNewSearch: (value: boolean) => void;
}> = ({setSearchArticle, setIsNewSearch}) => {
    const debouncedSearch = useMemo(() =>
        debounce((value: string) => {
            if (value.length > 2) {
                setSearchArticle(value.toLowerCase());
                setIsNewSearch(true);
            } else {
                setSearchArticle("");
            }
        }, 1000), [setSearchArticle, setIsNewSearch]);

    return (
        <input
            className="block w-full rounded-lg border border-gray-200 p-3 text-gray-600 hover:border-black"
            type="search"
            onChange={(event) => debouncedSearch(event.target.value)}
            placeholder="Cauta codul, denumirea sau gama unui articol..."
        />
    );
};

function CincuPricesPage() {
    const [searchArticle, setSearchArticle] = useState("");
    const [isNewSearch, setIsNewSearch] = useState(false);
    const [items, setItems] = useState<ProductPrice[]>([]);
    const [draftPrices, setDraftPrices] = useState<Record<number, string>>({});
    const [currentPage, setCurrentPage] = useState(0);
    const [inputValue, setInputValue] = useState(1);
    const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [savingArticleId, setSavingArticleId] = useState<number | null>(null);
    const pageSize = 20;

    useEffect(() => {
        setInputValue(currentPage + 1);
    }, [currentPage]);

    const handlePageChange = (selectedPage: number) => {
        setCurrentPage(selectedPage);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = Number(event.target.value);

        if (totalPages && value > totalPages) {
            value = totalPages;
        } else if (value < 1) {
            value = 1;
        }
        setInputValue(value);

        if (debounceTimer) clearTimeout(debounceTimer);

        const timer = setTimeout(() => {
            if (totalPages && value >= 1 && value <= totalPages) {
                handlePageChange(value - 1);
            }
        }, 500);

        setDebounceTimer(timer);
    };

    useEffect(() => {
        if (searchArticle && isNewSearch && currentPage !== 0) {
            setCurrentPage(0);
            return;
        }

        const loadProducts = async () => {
            setIsLoading(true);
            try {
                const response = await ApiHttpClient().post<ArticleSearchResponse>(
                    `/umPrices/${UM_WORKING_POINT_ID}/search`,
                    {
                        pageNumber: currentPage + 1,
                        pageSize,
                        ...(searchArticle ? {
                            advancedSearch: {
                                fields: ["id", "longName", "hierarchyLocal.marketLabel"],
                                keyword: searchArticle,
                            },
                        } : {}),
                    }
                );
                const products = (response.data.items || []).map((item) => ({
                    id: item.id,
                    name: item.displayName || item.longName || item.shortName || "",
                    gama: item.hierarchyLocal?.marketLabel || "",
                    price: item.thresholdPrice1 ?? 0,
                    vatPercent: item.vatPercent ?? 0,
                }));
                setItems(products);
                setTotalPages(response.data.totalPages || 0);
                setDraftPrices(Object.fromEntries(products.map((item) => [item.id, item.price.toFixed(2)])));
                setIsNewSearch(false);
            } catch (error: any) {
                setItems([]);
                setTotalPages(0);
                toast({description: error.response?.data?.title || "Nu s-au putut incarca produsele."});
            } finally {
                setIsLoading(false);
            }
        };

        loadProducts();
    }, [currentPage, searchArticle, isNewSearch]);

    const savePrice = async (articleId: number, price: number) => {
        setSavingArticleId(articleId);
        try {
            await ApiHttpClient().post("/salesPrice", {
                salesPrices: [{
                    id: articleId,
                    thresholdPrice1: price,
                    thresholdPrice2: null,
                    thresholdQuantity2: null,
                    thresholdPrice3: null,
                    thresholdQuantity3: null,
                    assortmentCode: UM_ASSORTMENT_CODE,
                    warehouseId: 0,
                }],
            });
            setItems((current) => current.map((item) => item.id === articleId ? {...item, price} : item));
            setDraftPrices((current) => ({...current, [articleId]: price.toFixed(2)}));
            toast({description: "Pretul a fost salvat."});
        } catch (error: any) {
            const errors = error.response?.data?.errors;
            const message = Array.isArray(errors) && errors.length > 0
                ? errors.map((entry: { errorMessage?: string } | string) =>
                    typeof entry === "string" ? entry : entry.errorMessage).filter(Boolean).join(" ")
                : error.response?.data?.title || "Eroare la salvarea pretului.";
            toast({description: message});
        } finally {
            setSavingArticleId(null);
        }
    };

    return (
        <>
            <Navbar/>
            <div className="flex flex-row items-start">
                <SideMenu/>
                <div className="flex w-full flex-col overflow-auto px-8 py-6">
                    <h1 className="text-3xl text-gray-900">Actualizare preturi</h1>
                    <p className="mt-4 max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Aceasta pagina are efect doar pentru CINCU.
                    </p>

                            <div className="mt-6 mb-5">
                                <SearchInput setSearchArticle={setSearchArticle} setIsNewSearch={setIsNewSearch}/>
                            </div>

                                <div className="mt-6">
                                    <h2 className="mb-3 text-lg font-medium text-gray-900">Produse</h2>
                                    {isLoading ? (
                                        <p className="text-sm text-gray-500">Se incarca produsele...</p>
                                    ) : items.length === 0 ? (
                                        <p className="text-sm text-gray-500">Nu exista produse pentru selectia curenta.</p>
                                    ) : (
                                        <div className="overflow-x-auto rounded-xl border border-gray-100">
                                            <table className="min-w-full divide-y divide-gray-200 bg-white text-sm">
                                                <thead className="text-left">
                                                <tr>
                                                    <th className="px-4 py-3 font-medium text-gray-900">Cod articol</th>
                                                    <th className="px-4 py-3 font-medium text-gray-900">Denumire</th>
                                                    <th className="px-4 py-3 font-medium text-gray-900">Gama</th>
                                                    <th className="px-4 py-3 font-medium text-gray-900">Pret fara TVA</th>
                                                    <th className="px-4 py-3 font-medium text-gray-900">Pret cu TVA</th>
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                {items.map((item) => {
                                                    const draft = draftPrices[item.id] ?? "";
                                                    const numericPrice = Number(draft.replace(",", "."));
                                                    const priceWithVat = Number.isNaN(numericPrice)
                                                        ? null
                                                        : numericPrice * (1 + item.vatPercent / 100);
                                                    return (
                                                        <tr key={item.id}>
                                                            <td className="whitespace-nowrap px-4 py-3 text-gray-900">{item.id}</td>
                                                            <td className="px-4 py-3 text-gray-700">{item.name}</td>
                                                            <td className="px-4 py-3 text-gray-700">{item.gama || "-"}</td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={draft}
                                                                        onChange={(event) => setDraftPrices((current) => ({
                                                                            ...current,
                                                                            [item.id]: event.target.value,
                                                                        }))}
                                                                        className="w-28 rounded-lg border border-gray-200 px-3 py-1.5 text-right text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                                    />
                                                                    {draft.trim() !== "" && !Number.isNaN(numericPrice) && numericPrice.toFixed(2) !== item.price.toFixed(2) && (
                                                                        <button
                                                                            type="button"
                                                                            disabled={savingArticleId === item.id}
                                                                            onClick={() => savePrice(item.id, numericPrice)}
                                                                            className="rounded-md ml-3 border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                                        >
                                                                            {savingArticleId === item.id ? "..." : "Salveaza"}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                                                                {priceWithVat == null ? "-" : `${priceWithVat.toFixed(2)} RON`}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {totalPages > 0 && (
                                        <div className="text-center mt-8 mb-2">
                                            <ul className="flex justify-center gap-1 text-gray-900 mt-3">
                                                <li>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            if (currentPage > 0) handlePageChange(currentPage - 1);
                                                        }}
                                                        className="grid mr-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                                        aria-label="Previous page"
                                                        disabled={currentPage === 0}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="size-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                        </svg>
                                                    </button>
                                                </li>
                                                <li>
                                                    <label htmlFor="Page" className="flex items-center gap-1">
                                                        <span className="sr-only">Pagina</span>
                                                        <input
                                                            type="number"
                                                            id="Page"
                                                            value={inputValue}
                                                            min={1}
                                                            max={totalPages}
                                                            onChange={handleInputChange}
                                                            className="h-8 w-10 rounded border-gray-300 text-center sm:text-sm"
                                                        />
                                                        <span className="text-sm">/ {totalPages}</span>
                                                    </label>
                                                </li>
                                                <li>
                                                    <button
                                                        type="button"
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            if (currentPage < totalPages - 1) handlePageChange(currentPage + 1);
                                                        }}
                                                        className="grid ml-2 size-8 place-content-center rounded border border-gray-200 transition-colors hover:bg-gray-50 rtl:rotate-180"
                                                        aria-label="Next page"
                                                        disabled={currentPage === totalPages - 1}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="size-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
                                                        </svg>
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                </div>
            </div>
        </>
    );
}

export default CincuPricesPage;
